'use client';

import { useEffect, useState } from 'react';
import { Container } from '@/atoms/Container/Container';
import { PostThreadSpacer } from '@/atoms/PostThreadSpacer/PostThreadSpacer';
import { useNestedReplies } from '@/hooks/useNestedReplies/useNestedReplies';
import { AUTO_COLLAPSE_THRESHOLD, DEFAULT_MAX_DEPTH } from '@/hooks/useNestedReplies/useNestedReplies.constants';
import { usePostNavigation } from '@/hooks/usePostNavigation/usePostNavigation';
import { cn } from '@/libs/utils/utils';
import { ShowMoreReplies } from '@/molecules/ShowMoreReplies/ShowMoreReplies';
import { ThreadExpandToggle } from '@/molecules/ThreadExpandToggle/ThreadExpandToggle';
import { PostMain } from '../PostMain/PostMain';

interface ReplyWithNestedProps {
  /** The composite ID of the reply post */
  replyId: string;
  /** Whether this is the last reply in the main list */
  isLastReply?: boolean;
  /** Current nesting depth (to prevent infinite nesting) */
  depth?: number;
  /** Maximum nesting depth allowed */
  maxDepth?: number;
  /** 0-based position among nested siblings (only meaningful at depth > 0). Used for aria-posinset. */
  nestedIndex?: number;
  /** Total nested-sibling count, including not-yet-loaded replies when known. Used for aria-setsize. */
  nestedSetSize?: number;
}

/**
 * ReplyWithNested Organism
 *
 * Displays a reply post with its nested sub-replies (recursive).
 * Each instance manages its own expand/collapse state:
 * - Replies with ≤3 sub-replies start expanded
 * - Replies with 4+ sub-replies start collapsed
 *
 * Recurses up to maxDepth levels (default 3).
 */
export function ReplyWithNested({
  replyId,
  isLastReply = false,
  depth = 0,
  maxDepth = DEFAULT_MAX_DEPTH,
  nestedIndex,
  nestedSetSize,
}: ReplyWithNestedProps) {
  const { handlePostKeyDown } = usePostNavigation();
  const { nestedReplyIds, hasMoreReplies, hasNestedReplies, replyCount, isExpandingAll, expandAll } = useNestedReplies(
    replyId,
    { depth, maxDepth },
  );

  // Local expand/collapse state — set once when replies are first available.
  // Until initialized, we skip rendering the nested section to avoid a flash
  // (starting collapsed then immediately animating to expanded).
  // Use replyCount (from postCounts) when available, otherwise fall back to the
  // number of locally cached reply IDs — postCounts can lag behind the cache.
  const [initialized, setInitialized] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const effectiveReplyCount = replyCount > 0 ? replyCount : nestedReplyIds.length;

  useEffect(() => {
    if (!initialized && effectiveReplyCount > 0) {
      setInitialized(true);
      setExpanded(effectiveReplyCount < AUTO_COLLAPSE_THRESHOLD);
    }
  }, [initialized, effectiveReplyCount]);

  const canShowToggle = hasNestedReplies && depth < maxDepth;

  return (
    <Container overrideDefaults data-cy="thread-reply">
      {/* Main reply */}
      <PostThreadSpacer />
      <Container overrideDefaults className={canShowToggle ? 'relative' : undefined}>
        <Container
          overrideDefaults
          data-post-list-card={depth > 0 ? 'true' : undefined}
          role={depth > 0 ? 'article' : undefined}
          aria-posinset={depth > 0 && nestedIndex !== undefined ? nestedIndex + 1 : undefined}
          aria-setsize={depth > 0 ? nestedSetSize : undefined}
          tabIndex={depth > 0 ? 0 : undefined}
          onKeyDown={depth > 0 ? (e) => handlePostKeyDown(replyId, e) : undefined}
          className={depth > 0 ? 'rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring' : undefined}
        >
          <PostMain postId={replyId} isReply={true} isLastReply={isLastReply} />
        </Container>
        {/* Toggle on the connector line, just above the rounded corner */}
        {canShowToggle && (
          <Container overrideDefaults className="absolute bottom-[24px] left-0 z-10 -translate-x-1/2">
            <ThreadExpandToggle expanded={expanded} onToggle={() => setExpanded((prev) => !prev)} />
          </Container>
        )}
      </Container>

      {/* Nested sub-replies — animated expand/collapse (skip until initial state is set) */}
      {hasNestedReplies && initialized && (
        <Container
          overrideDefaults
          className={`grid transition-all duration-300 ease-in-out ${
            expanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
          }`}
        >
          <Container overrideDefaults className="overflow-hidden">
            <Container overrideDefaults className="flex">
              {/* Continuation line (only when not the last sibling) */}
              {!isLastReply && <Container overrideDefaults className="w-3 shrink-0 border-l border-border" />}

              {/* Indented sub-replies */}
              <Container overrideDefaults className={cn('min-w-0 flex-1', isLastReply ? 'ml-6' : 'ml-3')}>
                {nestedReplyIds.map((nestedId, index) => {
                  const isLastNested = index === nestedReplyIds.length - 1 && !hasMoreReplies;
                  return (
                    <ReplyWithNested
                      key={nestedId}
                      replyId={nestedId}
                      isLastReply={isLastNested}
                      depth={depth + 1}
                      maxDepth={maxDepth}
                      nestedIndex={index}
                      nestedSetSize={effectiveReplyCount}
                    />
                  );
                })}

                {/* "+N more replies" if there are more nested replies */}
                {hasMoreReplies && !isExpandingAll && (
                  <ShowMoreReplies
                    count={Math.max(0, replyCount - nestedReplyIds.length)}
                    onClick={expandAll}
                    isLast={true}
                  />
                )}
              </Container>
            </Container>
          </Container>
        </Container>
      )}
    </Container>
  );
}
